import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  FileText,
  Loader2,
  MapPin,
  Pause,
  PenLine,
  Pill,
  Phone,
  Play,
  Plus,
  Save,
  Stethoscope,
  Upload,
  UserRound,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import DentalChart from './DentalChart';
import DektalInkpadWorkspace from './DektalInkpadWorkspace';
import AppointmentOverrunPrompt from './AppointmentOverrunPrompt';

const DOCTOR_SUMMARY_HEADING = 'Dental Chart Findings';
const DOCTOR_NOTES_HEADING = 'Additional Doctor Notes';
const MEDICINE_FREQUENCY_OPTIONS = [
  { value: 'once-daily', label: 'Once daily' },
  { value: 'twice-daily', label: 'Twice daily' },
  { value: 'thrice-daily', label: 'Thrice daily' },
  { value: 'custom', label: 'Custom' },
];

function createMedicineRow() {
  return {
    id: `medicine-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    frequency: 'once-daily',
    customFrequency: '',
  };
}

function getPrescriptionPadOpenError(err, fallback = 'Failed to open Prescription Pad') {
  const status = err?.response?.status;
  const backendError = err?.response?.data?.error;

  if (status === 400) {
    return backendError || 'Prescription bootstrap failed. Please check the patient and appointment link.';
  }

  if (status === 401) {
    return 'Your login session is not active. Please login again and reopen the Prescription Pad.';
  }

  if (status === 403) {
    return 'You do not have permission to open this Prescription Pad.';
  }

  if (status === 404 && backendError === 'Patient not found') {
    return 'Patient not found in the current clinic. Please login to the clinic that created this appointment or open the patient from the current clinic.';
  }

  if (status === 404) {
    return backendError || 'Prescription record was not found in the current clinic.';
  }

  return backendError || fallback;
}

function formatTime(totalSeconds) {
  const safeSeconds = Number.isFinite(Number(totalSeconds)) ? Number(totalSeconds) : 0;
  const minutes = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(safeSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function getTomorrowDateValue() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatCurrency(value) {
  return `Rs ${Math.round(toNumber(value, 0)).toLocaleString('en-IN')}`;
}

function getProposedEstimatedFee(item = {}) {
  return item.proposedEstimatedFee == null ? '' : String(item.proposedEstimatedFee);
}

function formatPlanCurrency(value, currencyCode = 'INR') {
  if (value === '' || value == null) return '—';
  const code = String(currencyCode || 'INR').toUpperCase();
  const amount = toNumber(value, 0);
  if (code === 'INR') return `Rs ${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  return `${code} ${amount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`;
}

function escapePrintTitle(value) {
  return String(value || 'Treatment Plan')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function buildTreatmentPlanPrintHtml(documentMarkup, title = 'Treatment Plan') {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapePrintTitle(title)}</title>
    <style>
      @page { size: A4 portrait; margin: 13mm; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; color: #172033; background: #fff; font: 10.5pt Arial, Helvetica, sans-serif; }
      .treatment-plan-print { width: 100%; }
      .treatment-plan-print__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; padding-bottom: 14px; border-bottom: 2px solid #243b6b; }
      .treatment-plan-print__clinic { display: flex; align-items: flex-start; gap: 12px; }
      .treatment-plan-print__logo { width: 58px; height: 58px; border-radius: 10px; object-fit: contain; }
      .treatment-plan-print h1 { margin: 0; color: #172554; font-size: 20pt; line-height: 1.2; }
      .treatment-plan-print__clinic p, .treatment-plan-print__title p { margin: 4px 0 0; color: #526078; line-height: 1.4; }
      .treatment-plan-print__title { min-width: 150px; text-align: right; }
      .treatment-plan-print__title strong { color: #172554; font-size: 14pt; }
      .treatment-plan-print__patient { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; margin: 18px 0; padding: 12px 14px; border: 1px solid #d7deea; border-radius: 8px; background: #f8fafc; }
      .treatment-plan-print__patient p { margin: 0; }
      .treatment-plan-print__summary { margin: 14px 0 18px; }
      .treatment-plan-print__summary h2 { margin: 0 0 8px; color: #172554; font-size: 11pt; text-transform: uppercase; letter-spacing: .06em; }
      .treatment-plan-print__summary-block { margin-top: 8px; padding-left: 10px; border-left: 3px solid #c7d2fe; white-space: pre-wrap; line-height: 1.5; }
      .treatment-plan-print table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 14px; font-size: 9.5pt; }
      .treatment-plan-print th, .treatment-plan-print td { border: 1px solid #aeb9ca; padding: 7px 6px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
      .treatment-plan-print th { color: #172554; background: #eef2ff; font-weight: 700; }
      .treatment-plan-print th:nth-child(1), .treatment-plan-print td:nth-child(1) { width: 8%; text-align: center; }
      .treatment-plan-print th:nth-child(2), .treatment-plan-print td:nth-child(2) { width: 13%; }
      .treatment-plan-print th:nth-child(3), .treatment-plan-print td:nth-child(3) { width: 27%; }
      .treatment-plan-print th:nth-child(4), .treatment-plan-print td:nth-child(4) { width: 12%; }
      .treatment-plan-print th:nth-child(5), .treatment-plan-print td:nth-child(5) { width: 17%; }
      .treatment-plan-print__total { margin: 16px 0 0; padding: 10px 12px; text-align: right; color: #172554; background: #f8fafc; border: 1px solid #d7deea; }
      .treatment-plan-print footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #aeb9ca; }
      .treatment-plan-print__signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; margin-top: 28px; }
      .treatment-plan-print__signature { padding-top: 28px; border-top: 1px solid #64748b; }
      .treatment-plan-print__disclaimer { margin: 28px 0 0; color: #64748b; font-size: 8.5pt; line-height: 1.45; }
      tr, .treatment-plan-print__patient, .treatment-plan-print__summary, footer { break-inside: avoid; }
      @media print { html, body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
    </style>
  </head>
  <body>${documentMarkup}</body>
</html>`;
}

export function openTreatmentPlanPrintDocument(documentNode, openWindow = () => window.open('', '_blank', 'width=960,height=760')) {
  if (!documentNode) return false;
  const printWindow = openWindow();
  if (!printWindow) return false;
  printWindow.opener = null;
  printWindow.document.open();
  printWindow.document.write(buildTreatmentPlanPrintHtml(documentNode.outerHTML, 'Treatment Plan'));
  printWindow.document.close();

  const printWhenReady = async () => {
    const images = Array.from(printWindow.document.images || []);
    await Promise.all(images.map((image) => image.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      })));
    if (printWindow.document.fonts?.ready) await printWindow.document.fonts.ready;
    printWindow.focus();
    printWindow.print();
  };

  if (printWindow.document.readyState === 'complete') {
    window.setTimeout(printWhenReady, 0);
  } else {
    printWindow.addEventListener('load', printWhenReady, { once: true });
  }
  return true;
}

function getTreatmentPrice(treatment) {
  return (
    treatment?.sellingPrice ??
    treatment?.selling_price ??
    treatment?.price ??
    treatment?.amount ??
    0
  );
}

function isTreatmentPricingReady(treatment = {}) {
  return treatment.pricingReady !== false && toNumber(getTreatmentPrice(treatment), 0) > 0;
}

function getTreatmentPriceLabel(treatment = {}) {
  return isTreatmentPricingReady(treatment)
    ? formatCurrency(getTreatmentPrice(treatment))
    : 'Not ready';
}

function getTreatmentGroupLabel(treatment = {}) {
  return String(
    treatment.domain ||
      treatment.domainName ||
      treatment.category ||
      treatment.group ||
      'Other'
  ).trim() || 'Other';
}

function getPlanItemCatalogId(item = {}) {
  if (item.sourceType === 'custom' || item.customTreatmentId || item.CustomTreatment?.id) {
    return `custom-${item.customTreatmentId || item.CustomTreatment?.id}`;
  }
  return item.treatmentId || item.Treatment?.id;
}

function getPlanItemName(item = {}) {
  return item.treatmentNameSnapshot || item.Treatment?.name || item.CustomTreatment?.treatmentName || 'Treatment';
}

function extractManualDoctorNotes(rawRemarks) {
  const normalized = String(rawRemarks || '').replace(/\r\n/g, '\n').trim();

  if (!normalized) {
    return '';
  }

  const marker = `${DOCTOR_NOTES_HEADING}\n`;
  const markerIndex = normalized.indexOf(marker);

  if (markerIndex >= 0) {
    return normalized.slice(markerIndex + marker.length).trim();
  }

  return normalized;
}

function buildDoctorFindingsValue(chartSummary, manualNotes) {
  const cleanSummary = String(chartSummary || '').trim();
  const normalizedNotes = String(manualNotes || '').replace(/\r\n/g, '\n');
  const hasVisibleNotes = normalizedNotes.trim().length > 0;

  if (!cleanSummary) {
    return normalizedNotes;
  }

  return `${DOCTOR_SUMMARY_HEADING}\n${cleanSummary}\n\n${DOCTOR_NOTES_HEADING}\n${
    hasVisibleNotes ? normalizedNotes : ''
  }`;
}

function readManualNotesFromEditor(value, chartSummary) {
  const normalized = String(value || '').replace(/\r\n/g, '\n');
  const cleanSummary = String(chartSummary || '').trim();

  if (!cleanSummary) {
    return normalized;
  }

  const marker = `${DOCTOR_NOTES_HEADING}\n`;
  const markerIndex = normalized.indexOf(marker);

  if (markerIndex >= 0) {
    return normalized.slice(markerIndex + marker.length);
  }

  if (normalized.startsWith(`${DOCTOR_SUMMARY_HEADING}\n`)) {
    return '';
  }

  return normalized;
}

function TreatmentWorkflowCard({
  title,
  tone,
  item,
  priceLookup,
  onStart,
  onOpenPause,
  onResume,
  onComplete,
  onNotesChange,
  onPlanChange,
  onPlanSave,
  savingPlan,
  onRequestRemove,
  removing,
}) {
  const treatmentCatalogId = getPlanItemCatalogId(item);
  const price = item.status === 'proposed'
    ? toNumber(getProposedEstimatedFee(item), 0)
    : (treatmentCatalogId ? getTreatmentPrice(priceLookup[treatmentCatalogId]) : 0);

  const statusToneMap = {
    proposed: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    ongoing: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    paused: 'border-amber-200 bg-amber-50 text-amber-700',
    completed: 'border-slate-200 bg-slate-100 text-slate-700',
  };

  return (
    <div data-treatment-plan-id={item.id} className="rounded-[22px] border border-slate-200/90 bg-white/95 p-4 shadow-[0_18px_38px_-34px_rgba(15,23,42,0.24)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">{title}</p>
          <h4 className="mt-1.5 text-[15px] font-semibold leading-snug text-slate-900">
            {getPlanItemName(item)}
          </h4>
          <div className="mt-2 flex flex-wrap items-center gap-2.5 text-[13px] font-medium text-slate-500">
            {item?.toothNumber ? <span>Tooth {item.toothNumber}</span> : null}
            {item.status !== 'proposed' && price > 0 ? <span>Estimated Fee: {formatCurrency(price)}</span> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-start">
          <div
            className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
              statusToneMap[item.status] || statusToneMap.proposed
            }`}
          >
            {item.status}
          </div>
          {item.status === 'proposed' && onRequestRemove ? (
            <button
              type="button"
              onClick={() => onRequestRemove(item)}
              disabled={removing}
              aria-label={`Remove proposed treatment ${getPlanItemName(item)}`}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition-colors hover:bg-rose-50 hover:text-rose-700 disabled:cursor-wait disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {item.status === 'proposed' && onPlanChange ? (
        <div className="mt-4 grid gap-3 rounded-[18px] border border-indigo-100 bg-indigo-50/30 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Sequence
            <input type="number" min="1" step="1" value={item.planSequence ?? ''} onChange={(event) => onPlanChange(item.id, 'planSequence', event.target.value)} onBlur={() => onPlanSave(item)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
          </label>
          <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Tooth / Site
            <input type="text" maxLength="120" value={item.toothNumber || ''} placeholder="16, Upper anterior, Full mouth" onChange={(event) => onPlanChange(item.id, 'toothNumber', event.target.value)} onBlur={() => onPlanSave(item)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
          </label>
          <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Estimated Fee
            <input type="number" min="0" step="any" inputMode="decimal" value={getProposedEstimatedFee(item)} onChange={(event) => onPlanChange(item.id, 'proposedEstimatedFee', event.target.value)} onBlur={() => onPlanSave(item)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
          </label>
          <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Notes
            <input type="text" maxLength="2000" value={item.notes || ''} placeholder="Treatment-plan note" onChange={(event) => onPlanChange(item.id, 'notes', event.target.value)} onBlur={() => onPlanSave(item)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
          </label>
          {savingPlan ? <p className="text-[11px] font-medium text-indigo-600 sm:col-span-2 lg:col-span-4">Saving treatment plan…</p> : null}
        </div>
      ) : null}

      {(item.status === 'ongoing' || item.status === 'paused' || item.status === 'completed') && (
        <div className="mt-4 flex flex-col gap-2 rounded-[18px] border border-slate-100 bg-slate-50/85 px-3.5 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Timer</p>
            <p className="mt-1 text-lg font-semibold text-slate-900 tabular-nums">{formatTime(item.timerSeconds || 0)}</p>
          </div>
          {item.status === 'ongoing' && (
            <div className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
              Live
            </div>
          )}
        </div>
      )}

      {(item.status === 'ongoing' || item.status === 'paused') && (
        <div className="mt-4">
          <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
            Clinical Notes / Pause Reason
          </label>
          <textarea
            value={item.notes || ''}
            onChange={(event) => onNotesChange(item.id, event.target.value)}
            placeholder="Add clinical notes, patient response, or pause reason..."
            className="mt-2.5 min-h-[96px] w-full rounded-[18px] border border-slate-200 bg-slate-50/80 px-3.5 py-3.5 text-[13px] font-medium text-slate-700 outline-none transition-all focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
          />
        </div>
      )}

      {item.status === 'completed' && item.notes ? (
        <div className="mt-4 rounded-[18px] border border-slate-100 bg-slate-50/80 px-3.5 py-3.5 text-[13px] font-medium text-slate-600">
          {item.notes}
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {item.status === 'proposed' && (
          <button
            type="button"
            onClick={() => onStart(item.id)}
            className={`tap-target inline-flex items-center justify-center gap-2 rounded-full px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_22px_-18px_rgba(15,23,42,0.45)] transition-colors ${tone}`}
          >
            <Play className="h-4 w-4 fill-current" />
            Start
          </button>
        )}

        {item.status === 'ongoing' && (
          <>
            <button
              type="button"
              onClick={() => onOpenPause(item)}
              className="tap-target inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_22px_-18px_rgba(15,23,42,0.45)] transition-colors hover:bg-amber-600"
            >
              <Pause className="h-4 w-4 fill-current" />
              Pause
            </button>
            <button
              type="button"
              onClick={() => onComplete(item.id, item.timerSeconds || 0, item.notes || '')}
              className="tap-target inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_22px_-18px_rgba(15,23,42,0.45)] transition-colors hover:bg-emerald-700"
            >
              <CheckCircle2 className="h-4 w-4" />
              Complete
            </button>
          </>
        )}

        {item.status === 'paused' && (
          <>
            <button
              type="button"
              onClick={() => onResume(item.id)}
              className="tap-target inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_22px_-18px_rgba(15,23,42,0.45)] transition-colors hover:bg-indigo-700"
            >
              <Play className="h-4 w-4 fill-current" />
              Resume
            </button>
            <button
              type="button"
              onClick={() => onComplete(item.id, item.timerSeconds || 0, item.notes || '')}
              className="tap-target inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_22px_-18px_rgba(15,23,42,0.45)] transition-colors hover:bg-emerald-700"
            >
              <CheckCircle2 className="h-4 w-4" />
              Complete
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function formatRecordDate(value) {
  if (!value) return 'Date not recorded';
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function MedicalRecordsList({ records = [] }) {
  if (!records.length) return <p className="text-sm text-slate-500">No previous medical history recorded.</p>;
  return records.map((row) => (
    <div key={`${row.prescriptionId}-${row.revisionId || row.date}-${row.medicalHistory}`} className="rounded-2xl border border-slate-200 p-4 text-sm">
      <p className="font-semibold text-slate-900">{formatRecordDate(row.date)}</p>
      <p className="mt-1 text-slate-500">{row.doctor || 'Doctor not recorded'}</p>
      <p className="mt-3 whitespace-pre-wrap leading-6 text-slate-700">{row.medicalHistory}</p>
    </div>
  ));
}

function DentalRecordsList({ records = [] }) {
  if (!records.length) return <p className="text-sm text-slate-500">No previous dental history recorded.</p>;
  return records.map((visit, index) => (
    <details key={visit.key || `${visit.prescriptionId}-${visit.date}-${index}`} open={index === 0} className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">
      <summary className="cursor-pointer list-none font-semibold text-slate-900">
        {formatRecordDate(visit.date)} <span className="font-normal text-slate-500">· {visit.doctor || 'Doctor not recorded'}</span>
      </summary>
      <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
        {(visit.doctorFindings || visit.toothFindings?.length) ? (
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Doctor&apos;s Findings</h3>
            {visit.doctorFindings ? <p className="mt-2 whitespace-pre-wrap leading-6 text-slate-700">{visit.doctorFindings}</p> : null}
            {visit.toothFindings?.length ? (
              <ul className="mt-2 space-y-2 text-slate-700">
                {visit.toothFindings.map((finding) => (
                  <li key={finding.id}>Tooth {finding.toothNumber} — {[...(finding.findings || []), finding.note].filter(Boolean).join('. ')}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
        {visit.completedTreatments?.length ? (
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Completed Treatments</h3>
            <ul className="mt-2 space-y-2 text-slate-700">
              {visit.completedTreatments.map((item) => <li key={item.id}>{item.treatmentName}{item.toothNumber ? ` — Tooth ${item.toothNumber}` : ''}{item.completedAt ? ` — ${formatRecordDate(item.completedAt)}` : ''}</li>)}
            </ul>
          </section>
        ) : null}
        {visit.inProgressTreatments?.length ? (
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-600">In Progress</h3>
            <ul className="mt-2 space-y-2 text-slate-700">
              {visit.inProgressTreatments.map((item) => <li key={item.id}>{item.treatmentName}{item.toothNumber ? ` — Tooth ${item.toothNumber}` : ''}</li>)}
            </ul>
          </section>
        ) : null}
        {(visit.clinicalNotes?.length || visit.completedTreatments?.some((item) => item.notes) || visit.inProgressTreatments?.some((item) => item.notes)) ? (
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Clinical Notes</h3>
            {[...(visit.clinicalNotes || []), ...(visit.completedTreatments || []).map((item) => item.notes), ...(visit.inProgressTreatments || []).map((item) => item.notes)].filter(Boolean).map((note, noteIndex) => <p key={`${note}-${noteIndex}`} className="mt-2 whitespace-pre-wrap leading-6 text-slate-700">{note}</p>)}
          </section>
        ) : null}
      </div>
    </details>
  ));
}

function AttachmentPreview({ file, onDelete }) {
  const isImage = String(file?.fileType || '').startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(file?.fileName || '');
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    const loadPreview = async () => {
      if (!isImage) {
        setPreviewUrl('');
        return;
      }

      if (!file?.id) {
        setPreviewUrl(file?.filePath || '');
        return;
      }

      try {
        const res = await api.get(`/upload/${file.id}/content`, {
          responseType: 'blob',
        });

        objectUrl = URL.createObjectURL(res.data);
        if (active) {
          setPreviewUrl(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        console.error('Failed to load attachment preview:', error);
        if (active) {
          setPreviewUrl('');
        }
      }
    };

    loadPreview();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file?.filePath, file?.id, isImage]);

  return (
    <div className="relative overflow-hidden rounded-[20px] border border-slate-200/90 bg-white shadow-[0_18px_36px_-34px_rgba(15,23,42,0.24)]">
      {isImage ? (
        previewUrl ? (
          <img
            src={previewUrl}
            alt={file.fileName || 'attachment'}
            className="h-28 w-full object-cover"
          />
        ) : (
          <div className="flex h-28 items-center justify-center bg-slate-50/80 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Preview loading
          </div>
        )
      ) : (
        <div className="flex h-28 items-center justify-center bg-slate-50/80">
          <div className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            {file.fileType || 'Document'}
          </div>
        </div>
      )}

      <div className="border-t border-slate-100 px-4 py-3">
        <p className="truncate text-[13px] font-medium text-slate-800">{file.fileName || 'Attachment'}</p>
      </div>

      <button
        type="button"
        onClick={() => onDelete(file.id)}
        className="absolute right-3 top-3 rounded-full bg-slate-900/78 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-slate-900"
      >
        Remove
      </button>
    </div>
  );
}

export default function PrescriptionPad() {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const appointmentId = searchParams.get('appointmentId');
  const prescriptionId = searchParams.get('id');
  const mode = searchParams.get('mode');
  const padContextLabel = mode === 'pad' ? 'clinical pad' : 'prescription record';
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [openingPad, setOpeningPad] = useState(Boolean(patientId && !prescriptionId));
  const [openingPadError, setOpeningPadError] = useState('');
  const [chiefComplaints, setChiefComplaints] = useState('');
  const [patientMedicalHistory, setPatientMedicalHistory] = useState('');
  const [doctorManualNotes, setDoctorManualNotes] = useState('');
  const [chartSummary, setChartSummary] = useState('');
  const [chartLoaded, setChartLoaded] = useState(false);
  const [medicines, setMedicines] = useState([createMedicineRow()]);
  const [generalAdvice, setGeneralAdvice] = useState('');
  const [treatments, setTreatments] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [treatmentsError, setTreatmentsError] = useState('');
  const [selectedTreatmentId, setSelectedTreatmentId] = useState('');
  const [addingPlan, setAddingPlan] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removingPlanId, setRemovingPlanId] = useState(null);
  const [savingPlanIds, setSavingPlanIds] = useState([]);
  const [proposed, setProposed] = useState([]);
  const [activeTreatments, setActiveTreatments] = useState([]);
  const [completedTreatments, setCompletedTreatments] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [pauseNote, setPauseNote] = useState('');
  const [followUpEnabled, setFollowUpEnabled] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(getTomorrowDateValue());
  const [followUpTime, setFollowUpTime] = useState('');
  const [schedulingFollowUp, setSchedulingFollowUp] = useState(false);
  const [standaloneFollowUpSaved, setStandaloneFollowUpSaved] = useState(null);
  const [clinicalMode, setClinicalMode] = useState('GUIDED');
  const [encounterId, setEncounterId] = useState(null);
  const [encounterState, setEncounterState] = useState('');
  const [clinicalIncomplete, setClinicalIncomplete] = useState(false);
  const [inkpadOpen, setInkpadOpen] = useState(false);
  const [longitudinalRecords, setLongitudinalRecords] = useState(null);
  const [recordsPanel, setRecordsPanel] = useState('');
  const autosaveTimer = useRef(null);
  const addPlanInFlight = useRef(false);
  const treatmentPlanPrintRef = useRef(null);

  const treatmentCatalog = useMemo(() => {
    const nextCatalog = {};
    treatments.forEach((treatment) => {
      nextCatalog[treatment.id] = treatment;
    });
    return nextCatalog;
  }, [treatments]);

  const selectedTreatment = useMemo(
    () => treatments.find((treatment) => String(treatment.id) === String(selectedTreatmentId)) || null,
    [selectedTreatmentId, treatments]
  );

  const treatmentsByGroup = useMemo(() => {
    const groups = [];
    const groupIndex = new Map();

    treatments.forEach((treatment) => {
      const label = getTreatmentGroupLabel(treatment);
      if (!groupIndex.has(label)) {
        groupIndex.set(label, groups.length);
        groups.push({ label, rows: [] });
      }
      groups[groupIndex.get(label)].rows.push(treatment);
    });

    return groups;
  }, [treatments]);

  const doctorFindingsValue = useMemo(
    () => buildDoctorFindingsValue(chartSummary, doctorManualNotes),
    [chartSummary, doctorManualNotes]
  );

  const proposedTotal = useMemo(
    () =>
      proposed.reduce((sum, item) => {
        return sum + toNumber(getProposedEstimatedFee(item), 0);
      }, 0),
    [proposed]
  );

  const orderedProposed = useMemo(
    () => [...proposed].sort((left, right) => {
      const leftSequence = Number(left.planSequence);
      const rightSequence = Number(right.planSequence);
      if (Number.isFinite(leftSequence) && Number.isFinite(rightSequence) && leftSequence !== rightSequence) return leftSequence - rightSequence;
      if (Number.isFinite(leftSequence) !== Number.isFinite(rightSequence)) return Number.isFinite(leftSequence) ? -1 : 1;
      return Number(left.id) - Number(right.id);
    }),
    [proposed]
  );

  useEffect(() => {
    if (patientId) {
      fetchPatient();
    }

    if (prescriptionId) {
      fetchPrescription();
      if (appointmentId) fetchEncounterContext();
    } else if (patientId) {
      fetchOrCreatePrescriptionForPatient();
    }

    fetchTreatments();
  }, [appointmentId, patientId, prescriptionId]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveTreatments((prev) =>
        prev.map((item) =>
          item.status === 'ongoing'
            ? { ...item, timerSeconds: toNumber(item.timerSeconds, 0) + 1 }
            : item
        )
      );
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!prescription?.id || !chartLoaded) return undefined;

    if (autosaveTimer.current) {
      window.clearTimeout(autosaveTimer.current);
    }

    autosaveTimer.current = window.setTimeout(() => {
      saveClinicalNotes(false);
    }, 700);

    return () => {
      if (autosaveTimer.current) {
        window.clearTimeout(autosaveTimer.current);
      }
    };
  }, [chiefComplaints, patientMedicalHistory, doctorFindingsValue, medicines, generalAdvice, prescription?.id, chartLoaded]);

  const fetchPatient = async () => {
    try {
      const res = await api.get(`/patients/${patientId}`);
      setPatient(res.data);
    } catch (err) {
      console.error(err);
      toast.error(getPrescriptionPadOpenError(err, 'Failed to load patient'));
    }
  };

  const openLongitudinalRecords = async (kind) => {
    if (!patientId) return;
    try {
      const { data } = await api.get(`/clinical-revisions/patients/${patientId}/longitudinal-records`);
      setLongitudinalRecords(data);
      setRecordsPanel(kind);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to load clinical records');
    }
  };

  const fetchEncounterContext = async () => {
    try {
      const encounterRes = await api.get(`/encounters/by-appointment/${appointmentId}`);
      const encounter = encounterRes.data?.encounter || null;
      setEncounterId(encounter?.id || null);
      setClinicalMode(encounter?.clinicalMode || 'GUIDED');
      setEncounterState(encounter?.state || '');
      setClinicalIncomplete(Boolean(encounter?.needsCompletionAt));
    } catch (error) {
      console.error(error);
    }
  };

  const applyPrescriptionData = (data) => {
    const allTreatments = data.PrescriptionTreatments || [];

    setPrescription(data);
    setChiefComplaints(data.medicalHistory || '');
    setPatientMedicalHistory(data.patientMedicalHistory || '');
    setDoctorManualNotes(extractManualDoctorNotes(data.remarks || ''));
    setMedicines(Array.isArray(data.medicines) && data.medicines.length > 0 ? data.medicines : [createMedicineRow()]);
    setGeneralAdvice(data.generalAdvice || '');
    setProposed(allTreatments.filter((item) => item.status === 'proposed'));
    setActiveTreatments(allTreatments.filter((item) => ['ongoing', 'paused'].includes(item.status)));
    setCompletedTreatments(allTreatments.filter((item) => item.status === 'completed'));
    setFiles(data.PrescriptionAttachments || []);
  };

  const fetchOrCreatePrescriptionForPatient = async () => {
    setOpeningPad(true);
    setOpeningPadError('');

    try {
      const res = await api.get(`/prescriptions/by-patient/${patientId}`, {
        params: appointmentId ? { appointmentId } : {},
      });

      applyPrescriptionData(res.data);
      if (appointmentId) {
        const encounterRes = await api.get(`/encounters/by-appointment/${appointmentId}`);
        const encounter = encounterRes.data?.encounter || null;
        setEncounterId(encounter?.id || null);
        setClinicalMode(encounter?.clinicalMode || 'GUIDED');
        setEncounterState(encounter?.state || '');
        setClinicalIncomplete(Boolean(encounter?.needsCompletionAt));
      }
      return res.data;
    } catch (err) {
      console.error(err);
      const message = getPrescriptionPadOpenError(err);
      setOpeningPadError(message);
      toast.error(message);
      return null;
    } finally {
      setOpeningPad(false);
    }
  };

  const fetchPrescription = async () => {
    try {
      const res = await api.get(`/prescriptions/${prescriptionId}`);
      const data = res.data;
      applyPrescriptionData(data);
      return data;
    } catch (err) {
      console.error(err);
      toast.error('Failed to load prescription');
      return null;
    }
  };

  const resolvePrescriptionForUpload = async () => {
    if (prescription?.id) return prescription;
    if (patientId) return fetchOrCreatePrescriptionForPatient();
    if (prescriptionId) return fetchPrescription();
    return null;
  };

  const fetchTreatments = async () => {
    setTreatmentsLoading(true);
    setTreatmentsError('');

    try {
      const res = await api.get('/prescriptions/treatment-catalog');
      const rows = Array.isArray(res.data) ? res.data : [];
      setTreatments(rows);

      if (rows.length === 0) {
        setTreatmentsError('No treatments found. Please check master treatment seed data.');
      } else {
        const firstNotReady = rows.find((treatment) => treatment.pricingReady === false);
        if (firstNotReady) {
          setTreatmentsError(firstNotReady.pricingMessage || 'Setup incomplete - pricing calculation not ready.');
        }
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.error || 'Failed to load treatments';
      setTreatments([]);
      setTreatmentsError(message);
      toast.error(message);
    } finally {
      setTreatmentsLoading(false);
    }
  };

  const saveClinicalNotes = async (showToast = false) => {
    if (!prescription?.id) return;

    try {
      setSavingNotes(true);
      await api.put(`/prescriptions/${prescription.id}`, {
        medicalHistory: chiefComplaints,
        patientMedicalHistory: patientMedicalHistory,
        remarks: doctorFindingsValue,
        medicines,
        generalAdvice,
      });

      if (showToast) {
        toast.success('Clinical notes saved');
      }
    } catch (err) {
      console.error(err);
      if (showToast) {
        toast.error('Failed to save clinical notes');
      }
    } finally {
      setSavingNotes(false);
    }
  };

  const addProposedTreatment = async () => {
    if (!prescription?.id || !selectedTreatmentId || addingPlan || addPlanInFlight.current) return;

    try {
      addPlanInFlight.current = true;
      setAddingPlan(true);
      const res = await api.post('/prescriptions/treatment', {
        prescriptionId: prescription.id,
        patientId: patient?.id || (patientId ? Number(patientId) : null),
        appointmentId: appointmentId ? Number(appointmentId) : null,
        encounterId: encounterId ? Number(encounterId) : null,
        treatmentId: selectedTreatmentId,
        sourceType: selectedTreatment?.sourceType || 'master',
        customTreatmentId: selectedTreatment?.customTreatmentId || null,
        toothNumber: '',
        estimatedFee: getTreatmentPrice(selectedTreatment),
      });

      setProposed((prev) => prev.some((item) => String(item.id) === String(res.data.id)) ? prev : [...prev, res.data]);
      setSelectedTreatmentId('');
      toast.success('Treatment added to plan');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to add treatment');
    } finally {
      addPlanInFlight.current = false;
      setAddingPlan(false);
    }
  };

  const updateProposedPlanField = (treatmentEntryId, field, value) => {
    setProposed((prev) => prev.map((item) => (
      String(item.id) === String(treatmentEntryId) ? { ...item, [field]: value } : item
    )));
  };

  const saveProposedPlanItem = async (item) => {
    if (!item?.id || item.status !== 'proposed' || savingPlanIds.includes(item.id)) return;
    try {
      setSavingPlanIds((prev) => [...prev, item.id]);
      const { data } = await api.patch(`/prescriptions/treatment/${item.id}/plan`, {
        planSequence: item.planSequence,
        toothNumber: item.toothNumber || '',
        estimatedFee: getProposedEstimatedFee(item),
        notes: item.notes || '',
      });
      setProposed((prev) => prev.map((row) => String(row.id) === String(item.id) ? data : row));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to save treatment plan details');
      await fetchPrescription();
    } finally {
      setSavingPlanIds((prev) => prev.filter((id) => String(id) !== String(item.id)));
    }
  };

  const removeProposedTreatment = async () => {
    if (!removeTarget?.id || removeTarget.status !== 'proposed' || removingPlanId) return;
    try {
      setRemovingPlanId(removeTarget.id);
      await api.delete(`/prescriptions/treatment/${removeTarget.id}`);
      setProposed((prev) => prev.filter((item) => String(item.id) !== String(removeTarget.id)));
      setRemoveTarget(null);
      toast.success('Proposed treatment removed');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to remove proposed treatment');
    } finally {
      setRemovingPlanId(null);
    }
  };

  const startTreatment = async (treatmentEntryId) => {
    try {
      const res = await api.put(`/prescriptions/treatment/${treatmentEntryId}`, { status: 'ongoing' });

      setProposed((prev) => prev.filter((item) => item.id !== treatmentEntryId));
      setActiveTreatments((prev) => [...prev, res.data]);
      toast.success('Treatment started');
    } catch (err) {
      console.error(err);
      toast.error('Failed to start treatment');
    }
  };

  const pauseTreatment = async (treatmentEntryId, timerSeconds, notes) => {
    try {
      const res = await api.put(`/prescriptions/treatment/${treatmentEntryId}`, {
        status: 'paused',
        timerSeconds,
        notes,
      });

      setActiveTreatments((prev) =>
        prev.map((item) => (item.id === treatmentEntryId ? res.data : item))
      );
      toast.success('Treatment paused');
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Failed to pause treatment');
      return false;
    }
  };

  const resumeTreatment = async (treatmentEntryId) => {
    try {
      const res = await api.put(`/prescriptions/treatment/${treatmentEntryId}`, {
        status: 'ongoing',
      });

      setActiveTreatments((prev) =>
        prev.map((item) => (item.id === treatmentEntryId ? res.data : item))
      );
      toast.success('Treatment resumed');
    } catch (err) {
      console.error(err);
      toast.error('Failed to resume treatment');
    }
  };

  const updateTreatmentNotes = (treatmentEntryId, notes) => {
    setActiveTreatments((prev) =>
      prev.map((item) => (item.id === treatmentEntryId ? { ...item, notes } : item))
    );
  };

  const scheduleFollowUpAppointment = async (treatment) => {
    if (!followUpEnabled) {
      return null;
    }

    if (!patient?.id && !patientId) {
      throw new Error('Patient context not available for follow-up scheduling');
    }

    if (!followUpDate) {
      throw new Error('Follow-up date is required');
    }

    if (!followUpTime) {
      throw new Error('Follow-up time is required');
    }

    setSchedulingFollowUp(true);

    try {
      const res = await api.post('/follow-ups', {
        patientId: patient?.id || Number(patientId),
        prescriptionId: prescription?.id || null,
        dueDate: followUpDate,
        time: followUpTime,
        doctorAssigned: prescription?.treatingDoctorName || null,
        treatmentId: treatment?.treatmentId || null,
        prescriptionTreatmentId: treatment?.id || null,
        whatsappReminderEnabled: true,
      });

      return res.data;
    } finally {
      setSchedulingFollowUp(false);
    }
  };

  const scheduleFlexibleNextVisit = async () => {
    if (!patient?.id || !prescription?.id || !followUpDate || !followUpTime) { toast.error('Select the next appointment date and time'); return; }
    setSchedulingFollowUp(true);
    try {
      const { data } = await api.post('/follow-ups', {
        patientId: patient.id, prescriptionId: prescription.id, dueDate: followUpDate, time: followUpTime,
        doctorAssigned: prescription?.treatingDoctorName || null, whatsappReminderEnabled: true,
      });
      setStandaloneFollowUpSaved(data);
      toast.success(data?.existing ? 'This next visit is already scheduled.' : 'Next visit scheduled. Clinical documentation can remain in Work to Finish.');
    } catch (error) { toast.error(error.response?.data?.error || 'Failed to schedule next visit'); }
    finally { setSchedulingFollowUp(false); }
  };

  const persistActiveTreatmentSnapshots = async () => {
    const snapshotTargets = activeTreatments.filter((item) =>
      ['ongoing', 'paused'].includes(item.status)
    );

    if (snapshotTargets.length === 0) {
      return;
    }

    const responses = await Promise.all(
      snapshotTargets.map((item) =>
        api.put(`/prescriptions/treatment/${item.id}`, {
          status: item.status,
          timerSeconds: item.timerSeconds || 0,
          notes: item.notes || '',
          toothNumber: item.toothNumber || '',
        })
      )
    );

    setActiveTreatments(responses.map((response) => response.data));
  };

  const completeTreatment = async (treatmentEntryId, timerSeconds, notes) => {
    try {
      const res = await api.put(`/prescriptions/treatment/${treatmentEntryId}`, {
        status: 'completed',
        timerSeconds,
        notes,
      });

      setActiveTreatments((prev) => prev.filter((item) => item.id !== treatmentEntryId));
      setCompletedTreatments((prev) => [...prev, res.data]);
      if (res.data?.requiresBillingAndInventory) {
        setEncounterState('READY_FOR_BILLING');
        toast.success('New performed treatment requires billing and inventory completion before finalizing.');
      } else {
        toast.success('Treatment marked completed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to complete treatment');
    }
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    setUploading(true);
    try {
      const uploadPrescription = await resolvePrescriptionForUpload();
      if (!uploadPrescription?.id) {
        toast.error('Prescription is still loading. Please try uploading again.');
        return;
      }

      const formData = new FormData();
      formData.append('prescriptionId', uploadPrescription.id);
      selectedFiles.forEach((file) => formData.append('files', file));

      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setFiles((prev) => [...prev, ...(res.data || [])]);
      toast.success('Attachments uploaded');
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const deleteFile = (fileId) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const returnToDashboard = async () => {
    try {
      await persistActiveTreatmentSnapshots();
      await saveClinicalNotes(false);
      if (clinicalMode === 'FLEXIBLE' && encounterId) {
        const { data } = await api.post(`/encounters/${encounterId}/save-for-later`, { mode: 'FLEXIBLE' });
        setEncounterState(data?.encounter?.state || encounterState);
        setClinicalIncomplete(true);
        toast.success('Saved to Work to Finish. Resume without retyping.');
      } else {
        toast.success('Progress saved. Resume this case anytime from Dashboard.');
      }
    } catch (err) {
      console.error(err);
    } finally {
      navigate('/dashboard');
    }
  };

  const endConsultation = async () => {
    if (!prescription?.id) {
      toast.error('Prescription not loaded');
      return;
    }

    try {
      await persistActiveTreatmentSnapshots();

      await api.put(`/prescriptions/${prescription.id}`, {
        medicalHistory: chiefComplaints,
        patientMedicalHistory: patientMedicalHistory,
        remarks: doctorFindingsValue,
        medicines,
        generalAdvice,
      });

      const endResponse = await api.post('/prescriptions/end', {
        prescriptionId: prescription.id,
        appointmentId: appointmentId ? Number(appointmentId) : null,
        medicalHistory: chiefComplaints,
        patientMedicalHistory: patientMedicalHistory,
        remarks: doctorFindingsValue,
        medicines,
        generalAdvice,
        clinicalMode,
      });

      if (endResponse.data?.prescription?.followUpRequired) {
        toast.success('Consultation saved. Open treatments remain available for follow-up.');
      } else {
        toast.success('Consultation completed');
      }

      navigate(
        `/invoices/new?prescriptionId=${prescription.id}${appointmentId ? `&appointmentId=${appointmentId}` : ''}`
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to end consultation');
    }
  };

  const finishClinicalDocumentation = async () => {
    if (!encounterId || !prescription?.id) return;
    try {
      await persistActiveTreatmentSnapshots();
      await api.put(`/prescriptions/${prescription.id}`, {
        medicalHistory: chiefComplaints,
        patientMedicalHistory,
        remarks: doctorFindingsValue,
        medicines,
        generalAdvice,
      });
      const { data } = await api.post(`/encounters/${encounterId}/complete-documentation`);
      setEncounterState(data?.encounter?.state || encounterState);
      setClinicalIncomplete(false);
      toast.success('Clinical documentation marked complete.');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Unable to complete clinical documentation');
    }
  };

  const handleChartSummaryChange = (summary) => {
    setChartSummary(summary || '');
    setChartLoaded(true);
  };

  const handleDoctorFindingsChange = (value) => {
    setDoctorManualNotes(readManualNotesFromEditor(value, chartSummary));
  };

  const printTreatmentPlan = () => {
    const opened = openTreatmentPlanPrintDocument(treatmentPlanPrintRef.current);
    if (!opened) toast.error('Print preview was blocked. Please allow pop-ups and try again.');
  };

  const addMedicineRow = () => {
    setMedicines((prev) => [...prev, createMedicineRow()]);
  };

  const updateMedicineRow = (rowId, field, value) => {
    setMedicines((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const removeMedicineRow = (rowId) => {
    setMedicines((prev) => {
      const nextRows = prev.filter((row) => row.id !== rowId);
      return nextRows.length > 0 ? nextRows : [createMedicineRow()];
    });
  };

  const openPauseModal = (item) => {
    setPauseTarget(item);
    setPauseNote(item.notes || '');
    setFollowUpEnabled(false);
    setFollowUpDate(getTomorrowDateValue());
    setFollowUpTime('');
    setPauseModalOpen(true);
  };

  const handlePauseSubmit = async () => {
    if (!pauseTarget) return;

    const latestPauseTarget =
      activeTreatments.find((item) => item.id === pauseTarget.id) || pauseTarget;

    const didPause = await pauseTreatment(
      latestPauseTarget.id,
      latestPauseTarget.timerSeconds || 0,
      pauseNote
    );

    if (!didPause) {
      return;
    }

    if (followUpEnabled) {
      try {
        await scheduleFollowUpAppointment(latestPauseTarget);
        toast.success('Follow-up appointment scheduled');
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.error || err.message || 'Treatment paused, but follow-up scheduling failed');
      }
    }

    setPauseModalOpen(false);
    setPauseTarget(null);
    setPauseNote('');
    setFollowUpEnabled(false);
    setFollowUpDate(getTomorrowDateValue());
    setFollowUpTime('');
  };

  const patientDetails = [
    { label: 'Patient', value: patient?.name || '--', icon: UserRound },
    { label: 'Assigned Doctor', value: prescription?.treatingDoctorName || '--' },
    { label: 'Age', value: patient?.age || '--' },
    { label: 'WhatsApp', value: patient?.phone || '--', icon: Phone },
    { label: 'ID', value: patient?.patientId || '--' },
    { label: 'Area', value: patient?.address || '--', icon: MapPin },
  ];

  if (patientId && openingPad && !prescription?.id) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(224,231,255,0.34),_transparent_28%),linear-gradient(180deg,_#fbfcff_0%,_#f1f5f9_100%)] px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[900px] rounded-[30px] border border-white/90 bg-white/95 p-8 shadow-[0_34px_90px_-68px_rgba(15,23,42,0.4)]">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
            <h1 className="text-2xl font-semibold tracking-[-0.02em] text-slate-900">Opening Prescription Pad</h1>
          </div>
          <p className="mt-3 text-sm font-medium text-slate-500">
            Loading the patient and appointment context for this {padContextLabel}.
          </p>
        </div>
      </div>
    );
  }

  if (patientId && openingPadError && !prescription?.id) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(224,231,255,0.34),_transparent_28%),linear-gradient(180deg,_#fbfcff_0%,_#f1f5f9_100%)] px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[900px] rounded-[30px] border border-white/90 bg-white/95 p-8 shadow-[0_34px_90px_-68px_rgba(15,23,42,0.4)]">
          <h1 className="text-2xl font-semibold tracking-[-0.02em] text-slate-900">Prescription Pad Unavailable</h1>
          <p className="mt-3 text-sm font-medium text-slate-500">{openingPadError}</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_10px_24px_-22px_rgba(15,23,42,0.5)] transition-colors hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(224,231,255,0.34),_transparent_28%),linear-gradient(180deg,_#fbfcff_0%,_#f1f5f9_100%)] px-3 py-4 sm:px-6 sm:py-5 lg:px-8">
      <div className="mx-auto max-w-[1280px] overflow-hidden rounded-[24px] border border-white/90 bg-white/95 shadow-[0_34px_90px_-68px_rgba(15,23,42,0.4)] backdrop-blur sm:rounded-[30px]">
        <div className="grid gap-3 border-b border-slate-100 px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center sm:px-8 lg:grid-cols-[1fr_auto_1fr]">
          <h1 className="text-[26px] font-semibold tracking-[-0.02em] text-slate-900 sm:text-[30px]">
            Prescription Pad
          </h1>

          <div className="inline-flex w-fit rounded-full border border-slate-200 bg-slate-50 p-1 text-[11px] font-semibold sm:justify-self-end lg:justify-self-center">
              {['GUIDED', 'FLEXIBLE'].map((mode) => <button key={mode} type="button" onClick={() => setClinicalMode(mode)} className={`rounded-full px-3 py-1.5 ${clinicalMode === mode ? 'bg-indigo-600 text-white' : 'text-slate-600'}`}>{mode === 'GUIDED' ? 'Guided Mode' : 'Flexible Mode'}</button>)}
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:col-span-2 sm:justify-end lg:col-span-1 lg:justify-self-end">
            <button
              type="button"
              disabled={!patient?.id || !prescription?.id}
              onClick={() => setInkpadOpen(true)}
              title="Open Dektal InkPad"
              aria-label="Open Dektal InkPad"
              className="tap-target inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3 text-[12px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PenLine className="h-4 w-4" />
              InkPad
            </button>
            <button
              type="button"
              onClick={() => saveClinicalNotes(true)}
              title="Save Notes"
              aria-label="Save Notes"
              disabled={savingNotes}
              className="tap-target inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
            >
              {savingNotes ? <Loader2 className="h-4 w-4 animate-spin text-indigo-600" /> : <Save className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={returnToDashboard}
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
              className="tap-target inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition-colors hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-8 px-4 py-5 sm:px-8 sm:py-8">
          <section>
            <div className="rounded-[24px] border border-slate-200 bg-[#F7F9FC] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] sm:px-5">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
                {patientDetails.map((detail) => {
                  const Icon = detail.icon;

                  return (
                    <div key={detail.label} className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        {detail.label}
                      </p>
                      <div className="mt-2.5 flex items-center gap-2">
                        {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : null}
                        <p className="truncate text-[13px] font-semibold uppercase tracking-[0.03em] text-slate-900">
                          {detail.value}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-[20px] font-semibold tracking-[-0.01em] text-slate-900">
                Chief Complaints by the patient in his own words
              </h2>
            </div>

            <textarea
              value={chiefComplaints}
              onChange={(event) => setChiefComplaints(event.target.value)}
              onBlur={() => saveClinicalNotes(false)}
              placeholder="Describe the patient's discomfort, pain point, or main concern exactly as reported."
              className="min-h-[138px] w-full rounded-[24px] border border-slate-200 bg-[#FBFCFF] px-4 py-4 text-[14px] font-medium text-slate-700 outline-none transition-all focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
            />
          </section>

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-[20px] font-semibold tracking-[-0.01em] text-slate-900">
                Medical History
              </h2>
              <div className="flex gap-2"><button type="button" onClick={() => openLongitudinalRecords('medical')} className="rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700">Medical Records</button><button type="button" onClick={() => openLongitudinalRecords('dental')} className="rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700">Dental Records</button></div>
            </div>

            <textarea
              value={patientMedicalHistory}
              onChange={(event) => setPatientMedicalHistory(event.target.value)}
              onBlur={() => saveClinicalNotes(false)}
              placeholder="Relevant medical conditions, allergies, medications, or past dental/medical history."
              className="min-h-[132px] w-full rounded-[24px] border border-slate-200 bg-[#FBFCFF] px-4 py-4 text-[14px] font-medium text-slate-700 outline-none transition-all focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
            />
          </section>

          <section className="space-y-5">
            <div>
              <h2 className="text-[20px] font-semibold tracking-[-0.01em] text-slate-900">
                Dental Observations and History
              </h2>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,_#ffffff_0%,_#fafcff_100%)] p-5 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)] sm:p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
                    Dental Chart
                  </h3>
                </div>

                <DentalChart patientId={patientId} onSummaryChange={handleChartSummaryChange} />
              </div>

              <div className="mt-6 border-t border-slate-100 pt-6">
                <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
                  Doctor&apos;s Findings
                </h3>

                <textarea
                  value={doctorFindingsValue}
                  onChange={(event) => handleDoctorFindingsChange(event.target.value)}
                  onBlur={() => saveClinicalNotes(false)}
                  placeholder="Add doctor observations, diagnostic notes, or next-step advice."
                  className="mt-4 min-h-[210px] w-full rounded-[22px] border border-slate-200 bg-[#FBFCFF] px-4 py-4 text-[14px] font-medium leading-7 text-slate-700 outline-none transition-all focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                />
              </div>
            </div>
          </section>

          {clinicalMode === 'FLEXIBLE' ? (
            <section className="space-y-4 rounded-[24px] border border-teal-200 bg-teal-50/50 p-5 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)] sm:p-6" aria-label="Flexible Mode Follow-up Next Visit">
              <div><h2 className="text-[18px] font-semibold text-slate-900">Follow-up / Next Visit</h2><p className="mt-1 text-xs text-slate-600">Schedule the patient’s next visit now. This does not require finishing the clinical document or removing it from Work to Finish.</p></div>
              <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]"><input aria-label="Flexible follow-up date" type="date" value={followUpDate} onChange={(event) => { setFollowUpDate(event.target.value); setStandaloneFollowUpSaved(null); }} className="rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm"/><input aria-label="Flexible follow-up time" type="time" value={followUpTime} onChange={(event) => { setFollowUpTime(event.target.value); setStandaloneFollowUpSaved(null); }} className="rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm"/><button type="button" onClick={scheduleFlexibleNextVisit} disabled={schedulingFollowUp || Boolean(standaloneFollowUpSaved)} className="rounded-[16px] bg-teal-700 px-4 py-3 text-xs font-bold uppercase tracking-wide text-white disabled:opacity-50">{schedulingFollowUp ? 'Scheduling…' : standaloneFollowUpSaved ? 'Scheduled' : 'Schedule Next Visit'}</button></div>
            </section>
          ) : null}

          <section className="space-y-6">
            <div>
              <h2 className="text-[20px] font-semibold tracking-[-0.01em] text-slate-900">
                Proposed Treatment
              </h2>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)] sm:p-6">
              <div className="grid gap-3.5 xl:grid-cols-[minmax(0,1fr)_180px_132px]">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Procedure Selection
                  </p>
                  <div className="mt-2.5 rounded-[20px] border border-slate-200 bg-[#FBFCFF] px-4 py-3.5">
                    <select
                      value={selectedTreatmentId}
                      onChange={(event) => setSelectedTreatmentId(event.target.value)}
                      disabled={treatmentsLoading}
                      className="w-full bg-transparent text-[14px] font-medium text-slate-900 outline-none"
                    >
                      <option value="">
                        {treatmentsLoading ? 'Loading treatments...' : 'Choose Treatment...'}
                      </option>
                      {!treatmentsLoading && treatments.length === 0 ? (
                        <option value="" disabled>
                          No treatments available
                        </option>
                      ) : null}
                      {treatmentsByGroup.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.rows.map((treatment) => (
                            <option key={treatment.id} value={treatment.id}>
                              {treatment.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  {treatmentsError ? (
                    <p className="mt-2 text-[12px] font-medium text-rose-600">{treatmentsError}</p>
                  ) : null}
                </div>

                <div>
                  <p className="text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Estimated Fee
                  </p>
                  <div className="mt-2.5 rounded-[20px] border border-slate-200 bg-[#FBFCFF] px-4 py-3.5 text-center">
                    <p className="text-[22px] font-semibold tracking-[-0.02em] text-slate-900">
                      {selectedTreatment ? getTreatmentPriceLabel(selectedTreatment) : 'Not ready'}
                    </p>
                  </div>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={addProposedTreatment}
                    disabled={!selectedTreatmentId || treatmentsLoading || addingPlan}
                    className="w-full rounded-[20px] bg-indigo-600 px-4 py-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-[0_14px_28px_-22px_rgba(79,70,229,0.72)] transition-all hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                  >
                    {addingPlan ? 'Adding...' : 'Add Plan'}
                  </button>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="rounded-[22px] border border-indigo-100 bg-indigo-50/40 p-4">
                  <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <Stethoscope className="h-4 w-4 text-indigo-600" />
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-700">Proposed</p>
                    </div>
                    {proposed.length > 0 ? (
                      <div className="rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-indigo-700">
                        Total Proposed: {formatCurrency(proposedTotal)}
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-4">
                    {proposed.length > 0 ? (
                      orderedProposed.map((item) => (
                        <TreatmentWorkflowCard
                          key={item.id}
                          title="Planned Treatment"
                          tone="bg-indigo-600 hover:bg-indigo-700"
                          item={item}
                          priceLookup={treatmentCatalog}
                          onStart={startTreatment}
                          onOpenPause={openPauseModal}
                          onResume={resumeTreatment}
                          onComplete={completeTreatment}
                          onNotesChange={updateTreatmentNotes}
                          onPlanChange={updateProposedPlanField}
                          onPlanSave={saveProposedPlanItem}
                          savingPlan={savingPlanIds.some((id) => String(id) === String(item.id))}
                          onRequestRemove={setRemoveTarget}
                          removing={String(removingPlanId) === String(item.id)}
                        />
                      ))
                    ) : (
                      <div className="rounded-[18px] border border-dashed border-indigo-200 bg-white/80 px-4 py-7 text-center text-[13px] font-medium text-indigo-500">
                        No proposed treatments yet.
                      </div>
                    )}
                  </div>
                </div>

                {activeTreatments.length > 0 ? (
                  <div className="rounded-[22px] border border-emerald-100 bg-emerald-50/40 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Activity className="h-4 w-4 text-emerald-600" />
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Ongoing / Paused</p>
                    </div>

                    <div className="space-y-4">
                      {activeTreatments.map((item) => (
                        <TreatmentWorkflowCard
                          key={item.id}
                          title={item.status === 'paused' ? 'Paused Treatment' : 'Active Treatment'}
                          tone="bg-emerald-600 hover:bg-emerald-700"
                          item={item}
                          priceLookup={treatmentCatalog}
                          onStart={startTreatment}
                          onOpenPause={openPauseModal}
                          onResume={resumeTreatment}
                          onComplete={completeTreatment}
                          onNotesChange={updateTreatmentNotes}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {completedTreatments.length > 0 ? (
                  <div className="rounded-[22px] border border-slate-200 bg-slate-50/65 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-slate-700" />
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">Completed</p>
                    </div>

                    <div className="space-y-4">
                      {completedTreatments.map((item) => (
                        <TreatmentWorkflowCard
                          key={item.id}
                          title="Completed Treatment"
                          tone="bg-slate-700 hover:bg-slate-800"
                          item={item}
                          priceLookup={treatmentCatalog}
                          onStart={startTreatment}
                          onOpenPause={openPauseModal}
                          onResume={resumeTreatment}
                          onComplete={completeTreatment}
                          onNotesChange={updateTreatmentNotes}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div>
              <h2 className="text-[20px] font-semibold tracking-[-0.01em] text-slate-900">
                Clinical Attachments / Old Records
              </h2>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)] sm:p-6">
              <div className="grid gap-5 lg:grid-cols-[210px_minmax(0,1fr)]">
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-[22px] border-2 border-dashed border-slate-200 bg-slate-50/80 px-5 py-7 text-center transition-colors hover:border-indigo-200 hover:bg-indigo-50/60">
                  <div className="rounded-full bg-white p-4 shadow-sm">
                    {uploading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    ) : (
                      <Upload className="h-6 w-6 text-indigo-600" />
                    )}
                  </div>
                  <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Attach old record / clinical file
                  </p>
                  <p className="mt-2 text-[13px] font-medium text-slate-500">
                    Old prescription photos, X-rays, lab reports, case photos, and scans
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>

                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {files.length > 0 ? (
                    files.map((file) => (
                      <AttachmentPreview key={file.id} file={file} onDelete={deleteFile} />
                    ))
                  ) : (
                    <div className="flex min-h-[168px] items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 text-center text-[13px] font-medium text-slate-400 sm:col-span-2 xl:col-span-3">
                      Uploads will appear here once added.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div>
              <h2 className="text-[20px] font-semibold tracking-[-0.01em] text-slate-900">
                Medical Advice
              </h2>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
              <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)] sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <Pill className="h-4 w-4 text-indigo-600" />
                    <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
                      Medicines
                    </h3>
                  </div>

                  <button
                    type="button"
                    onClick={addMedicineRow}
                    className="tap-target inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-700 transition hover:bg-indigo-100"
                  >
                    <Plus className="h-4 w-4" />
                    Add Medicine
                  </button>
                </div>

                <div className="mt-5 space-y-3">
                  {medicines.map((medicine, index) => (
                    <div
                      key={medicine.id}
                      className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4"
                    >
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1.2fr)_220px_auto]">
                        <input
                          type="text"
                          value={medicine.name || ''}
                          onChange={(event) => updateMedicineRow(medicine.id, 'name', event.target.value)}
                          placeholder={`Medicine ${index + 1}`}
                          className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                        />

                        <div className="space-y-2">
                          <select
                            value={medicine.frequency || 'once-daily'}
                            onChange={(event) => updateMedicineRow(medicine.id, 'frequency', event.target.value)}
                            className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                          >
                            {MEDICINE_FREQUENCY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>

                          {medicine.frequency === 'custom' ? (
                            <input
                              type="text"
                              value={medicine.customFrequency || ''}
                              onChange={(event) =>
                                updateMedicineRow(medicine.id, 'customFrequency', event.target.value)
                              }
                              placeholder="Custom frequency"
                              className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                            />
                          ) : null}
                        </div>

                        <div className="flex items-start justify-end md:justify-center">
                          <button
                            type="button"
                            onClick={() => removeMedicineRow(medicine.id)}
                            className="rounded-full border border-rose-200 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-600 transition hover:bg-rose-50"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)] sm:p-6">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
                    General Advice
                  </h3>
                </div>

                <textarea
                  value={generalAdvice}
                  onChange={(event) => setGeneralAdvice(event.target.value)}
                  onBlur={() => saveClinicalNotes(false)}
                  placeholder="Warm saline rinses, avoid chewing on that side, rest for 7 days, soft diet..."
                  className="mt-4 min-h-[260px] w-full rounded-[22px] border border-slate-200 bg-[#FBFCFF] px-4 py-4 text-[14px] font-medium leading-7 text-slate-700 outline-none transition-all focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                />
              </div>
            </div>
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,_#ffffff_0%,_#f8fafc_100%)] p-5 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)] sm:p-6">
            <article ref={treatmentPlanPrintRef} id="treatment-plan-print" className="treatment-plan-print">
              <header className="treatment-plan-print__header">
                <div className="treatment-plan-print__clinic">
                  {prescription?.clinicIdentity?.logo ? <img className="treatment-plan-print__logo" src={prescription.clinicIdentity.logo} alt="Clinic logo" /> : null}
                  <div>
                    <h1>{prescription?.clinicIdentity?.clinicName || patient?.clinicName || 'Treatment Plan'}</h1>
                    {prescription?.clinicIdentity?.address ? <p>{prescription.clinicIdentity.address}</p> : null}
                    {prescription?.clinicIdentity?.contact ? <p>Contact: {prescription.clinicIdentity.contact}</p> : null}
                    {prescription?.clinicIdentity?.licenseNumber ? <p>Registration: {prescription.clinicIdentity.licenseNumber}</p> : null}
                  </div>
                </div>
                <div className="treatment-plan-print__title"><strong>Treatment Plan</strong><p>{formatRecordDate(prescription?.date || prescription?.createdAt || new Date())}</p></div>
              </header>
              <section className="treatment-plan-print__patient"><p><b>Patient Name:</b> {patient?.name || prescription?.Patient?.name || '—'}</p><p><b>Patient ID:</b> {patient?.patientId || prescription?.Patient?.patientId || '—'}</p><p><b>Date:</b> {formatRecordDate(prescription?.date || prescription?.createdAt || new Date())}</p><p><b>Treating Doctor:</b> {prescription?.treatingDoctorName || prescription?.clinicIdentity?.leadDoctor || '—'}</p></section>
              {(chiefComplaints || doctorFindingsValue) ? <section className="treatment-plan-print__summary"><h2>Clinical Summary</h2>{chiefComplaints ? <div className="treatment-plan-print__summary-block"><b>Chief Complaint</b><br />{chiefComplaints}</div> : null}{doctorFindingsValue ? <div className="treatment-plan-print__summary-block"><b>Dental Findings / Diagnosis</b><br />{doctorFindingsValue}</div> : null}</section> : null}
              <table><thead><tr><th>Sequence</th><th>Tooth / Site</th><th>Proposed Treatment</th><th>Status</th><th>Estimated Fee</th><th>Notes</th></tr></thead><tbody>{orderedProposed.map((item) => <tr key={item.id}><td>{item.planSequence || '—'}</td><td>{item.toothNumber || '—'}</td><td>{getPlanItemName(item)}</td><td>Proposed</td><td>{formatPlanCurrency(getProposedEstimatedFee(item), item.currencyCode)}</td><td>{item.notes || '—'}</td></tr>)}</tbody></table>
              <p className="treatment-plan-print__total"><b>Total Estimated Treatment Amount:</b> {formatPlanCurrency(proposedTotal, orderedProposed.find((item) => item.currencyCode)?.currencyCode || 'INR')}</p>
              <footer><div className="treatment-plan-print__signatures"><div className="treatment-plan-print__signature">Treating Doctor / Signature</div><div className="treatment-plan-print__signature">Patient Acknowledgement / Signature</div></div><p className="treatment-plan-print__disclaimer">This treatment plan is based on the current clinical assessment. Treatment requirements and final charges may change if additional clinical findings arise.</p></footer>
            </article>
            {clinicalMode === 'FLEXIBLE' && clinicalIncomplete ? (
              <div className="mb-4 rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-800">
                This encounter remains in Work to Finish. Billing and inventory can continue while the clinical documentation stays open.
              </div>
            ) : null}
            <div className="flex flex-wrap justify-end gap-3">
              <button type="button" onClick={printTreatmentPlan} className="mr-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700">Print Treatment Plan</button>
              {clinicalMode === 'FLEXIBLE' && clinicalIncomplete ? (
                <button
                  type="button"
                  onClick={finishClinicalDocumentation}
                  className="tap-target inline-flex w-full items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700 transition-colors hover:bg-emerald-100 sm:w-auto"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Mark Documentation Complete
                </button>
              ) : null}
              {encounterState === 'READY_FOR_BILLING' ? (
                <button
                  type="button"
                  onClick={() => navigate(`/invoices/new?prescriptionId=${prescription.id}${appointmentId ? `&appointmentId=${appointmentId}` : ''}`)}
                  className="tap-target inline-flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-[0_14px_28px_-22px_rgba(79,70,229,0.75)] transition-colors hover:bg-indigo-700 sm:w-auto"
                >
                  Continue to Billing
                </button>
              ) : !['BILLED_PAYMENT_PENDING', 'INVENTORY_REVIEW', 'COMPLETED'].includes(encounterState) ? (
                <button
                  type="button"
                  onClick={endConsultation}
                  className="tap-target inline-flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-[0_14px_28px_-22px_rgba(79,70,229,0.75)] transition-colors hover:bg-indigo-700 sm:w-auto"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {clinicalMode === 'FLEXIBLE' ? 'Save Incomplete & Continue to Billing' : 'End Consultation and Continue to Invoice'}
                </button>
              ) : null}
            </div>
          </section>
        </div>
      </div>

      {recordsPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div><h2 className="text-xl font-semibold text-slate-900">{recordsPanel === 'medical' ? 'Medical Records' : 'Dental Records'}</h2><p className="mt-1 text-sm text-slate-500">Longitudinal clinical record for {longitudinalRecords?.patient?.name || patient?.name}</p></div>
              <button type="button" onClick={() => setRecordsPanel('')} className="rounded-full border px-3 py-1 text-sm">Close</button>
            </div>
            <div className="mt-5 space-y-3">{recordsPanel === 'medical' ? <MedicalRecordsList records={longitudinalRecords?.medicalRecords || []} /> : <DentalRecordsList records={longitudinalRecords?.dentalRecords || []} />}</div>
          </div>
        </div>
      )}

      {inkpadOpen && patient?.id && prescription?.id ? (
        <DektalInkpadWorkspace
          patient={patient}
          prescription={prescription}
          appointmentId={appointmentId}
          encounterId={encounterId}
          onClose={() => setInkpadOpen(false)}
        />
      ) : null}

      {appointmentId && encounterId ? (
        <AppointmentOverrunPrompt
          appointmentId={appointmentId}
          encounterId={encounterId}
        />
      ) : null}

      {removeTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-slate-900">Remove this proposed treatment?</h2>
            <p className="mt-2 text-sm text-slate-500">{getPlanItemName(removeTarget)}</p>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setRemoveTarget(null)} disabled={Boolean(removingPlanId)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
              <button type="button" onClick={removeProposedTreatment} disabled={Boolean(removingPlanId)} className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{removingPlanId ? 'Removing...' : 'Remove'}</button>
            </div>
          </div>
        </div>
      )}

      {pauseModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/55 px-2 py-4 backdrop-blur-sm sm:items-center sm:px-4">
          <div className="max-h-[calc(100dvh-1rem)] w-full max-w-lg overflow-y-auto rounded-[24px] border border-white/70 bg-white p-5 shadow-[0_36px_90px_-58px_rgba(15,23,42,0.58)] sm:rounded-[28px] sm:p-7">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Pause Treatment</p>
            <h3 className="mt-3 text-[24px] font-semibold tracking-[-0.01em] text-slate-900">
              Save the clinical reason before pausing
            </h3>
            <p className="mt-3 text-[13px] font-medium text-slate-500">
              This note stays attached to the treatment card and preserves the pause context.
            </p>

            <textarea
              value={pauseNote}
              onChange={(event) => setPauseNote(event.target.value)}
              placeholder="Patient requested a break, additional imaging needed, anaesthesia waiting time..."
              className="mt-5 min-h-[156px] w-full rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-[14px] font-medium text-slate-700 outline-none transition-all focus:border-amber-300 focus:bg-white focus:ring-4 focus:ring-amber-50"
            />

            <div className="mt-5 rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={followUpEnabled}
                  onChange={(event) => setFollowUpEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-[13px] font-semibold text-slate-700">
                  Schedule a follow-up appointment for this patient
                </span>
              </label>

              {followUpEnabled ? (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="pause-follow-up-date" className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Follow-up Date
                    </label>
                    <input
                      id="pause-follow-up-date"
                      type="date"
                      required={followUpEnabled}
                      value={followUpDate}
                      onChange={(event) => setFollowUpDate(event.target.value)}
                      className="mt-2 w-full rounded-[16px] border border-slate-200 bg-white px-3.5 py-3 text-[13px] font-medium text-slate-700 outline-none transition-all focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                    />
                  </div>

                  <div>
                    <label htmlFor="pause-follow-up-time" className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Follow-up Time
                    </label>
                    <input
                      id="pause-follow-up-time"
                      type="time"
                      required={followUpEnabled}
                      value={followUpTime}
                      onChange={(event) => setFollowUpTime(event.target.value)}
                      className="mt-2 w-full rounded-[16px] border border-slate-200 bg-white px-3.5 py-3 text-[13px] font-medium text-slate-700 outline-none transition-all focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mobile-stack-actions mt-5 flex flex-wrap justify-end gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setPauseModalOpen(false);
                  setPauseTarget(null);
                  setPauseNote('');
                  setFollowUpEnabled(false);
                  setFollowUpDate(getTomorrowDateValue());
                  setFollowUpTime('');
                }}
                className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePauseSubmit}
                disabled={schedulingFollowUp}
                className="rounded-full bg-amber-500 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_24px_-20px_rgba(15,23,42,0.45)] transition-colors hover:bg-amber-600"
              >
                {schedulingFollowUp ? 'Scheduling...' : 'Save & Pause'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
